import type { HSCandidate, HSPrediction } from './api';

// The HS hierarchy the model's ranking can be read at. DGML ranks hs6 codes only;
// hs4 and hs2 on each prediction are prefixes of that hs6, not separate predictions,
// so a coarser level is a roll-up of the hs6 list rather than a different answer.
export type HsLevel = 'hs6' | 'hs4' | 'hs2';

export const HS_LEVELS: { level: HsLevel; label: string }[] = [
  { level: 'hs6', label: '6 digits' },
  { level: 'hs4', label: '4 digits' },
  { level: 'hs2', label: '2 digits' },
];

export interface HsLevelPrediction {
  code: string;
  // The best cosine among the hs6 codes under this prefix, which is what a
  // nearest-prototype ranking means for the group: its closest member.
  cosine: number;
  // The summed probability of its members. Every code shares one softmax, so this
  // is the probability of the group as far as the returned codes go: a floor, since
  // codes outside the top k under the same prefix are not in the list.
  probability: number;
  // The hs6 codes that rolled up into it, best first; one of them at hs6
  members: HSPrediction[];
  definition: string | null;
  // Whether any member is a code the caller declared
  candidate: boolean;
}

// Collapses the ranked hs6 list to one entry per code at `level`, in the order the
// groups first appear. The list is best-first, so the first member of each group is
// its best cosine and the groups come out best-first too.
export function rollUpHsPredictions(predictions: HSPrediction[], level: HsLevel): HsLevelPrediction[] {
  const groups = new Map<string, HsLevelPrediction>();

  for (const prediction of predictions) {
    const code = prediction[level];
    const group = groups.get(code);

    if (group) {
      group.members.push(prediction);
      group.cosine = Math.max(group.cosine, prediction.cosine);
      group.probability += prediction.probability;
      group.candidate = group.candidate || prediction.candidate === true;
    } else {
      groups.set(code, {
        code,
        cosine: prediction.cosine,
        probability: prediction.probability,
        members: [prediction],
        definition: level === 'hs6' ? prediction.definition ?? null : null,
        candidate: prediction.candidate === true,
      });
    }
  }

  return Array.from(groups.values());
}

// The most specific level at which a declared code agrees with any suggestion, or
// null when none shares even its chapter. This is the tier live_hs_predictions
// scores a declaration on, so the card and the live flag read a code the same way.
export function agreementLevel(candidate: HSCandidate, predictions: HSPrediction[]): HsLevel | null {
  for (const { level } of HS_LEVELS) {
    const code = candidate[level];
    if (code !== null && predictions.some((prediction) => prediction[level] === code)) return level;
  }
  return null;
}
